/**
 * Servicio de Autenticación
 * Maneja login, logout, refresh token y obtención de usuario actual
 */
import api from './api';

/**
 * Iniciar sesión con email y contraseña
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña del usuario
 * @returns {Promise<Object>} Datos del usuario y tokens
 */
export const login = async (email, password) => {
  try {
    const response = await api.post('/auth/login', {
      email,
      password,
    });

    const { user, accessToken, refreshToken } = response.data;

    // Guardar tokens y usuario en localStorage
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response.data;
  } catch (error) {
    console.error('Error en login:', error);
    throw error;
  }
};

/**
 * Cerrar sesión del usuario
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      await api.post('/auth/logout', { refreshToken });
    }
  } catch (error) {
    console.error('Error en logout:', error);
  } finally {
    // Limpiar localStorage siempre
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }
};

/**
 * Refrescar el token de acceso
 * @param {string} refreshToken - Token de refresco
 * @returns {Promise<Object>} Nuevos tokens
 */
export const refreshToken = async (refreshToken) => {
  try {
    const response = await api.post('/auth/refresh', {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;

    // Actualizar tokens en localStorage
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }

    return response.data;
  } catch (error) {
    console.error('Error al refrescar token:', error);
    // Limpiar localStorage si falla el refresh
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    throw error;
  }
};

/**
 * Obtener información del usuario actual
 * @returns {Promise<Object>} Datos del usuario actual
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');

    // Actualizar usuario en localStorage
    if (response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }

    return response.data;
  } catch (error) {
    console.error('Error al obtener usuario actual:', error);
    throw error;
  }
};

/**
 * Cambia la contraseña del usuario actual
 * @param {string} passwordActual - Contraseña actual del usuario
 * @param {string} passwordNueva - Nueva contraseña
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const changePassword = async (passwordActual, passwordNueva) => {
  try {
    const response = await api.patch('/auth/change-password', {
      passwordActual,
      passwordNueva
    });
    return response.data;
  } catch (error) {
    console.error('Error al cambiar contraseña:', error);
    throw error;
  }
};

/**
 * Obtiene el historial de actividad reciente del usuario
 * @returns {Promise<Object>} Lista de actividades recientes
 */
export const getRecentActivity = async () => {
  try {
    const response = await api.get('/auth/recent-activity');
    return response.data;
  } catch (error) {
    console.error('Error al obtener actividad reciente:', error);
    throw error;
  }
};

/**
 * Verificar si hay una sesión activa
 * @returns {boolean} True si hay tokens válidos
 */
export const isAuthenticated = () => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  return !!(accessToken || refreshToken);
};

/**
 * Obtener usuario del localStorage
 * @returns {Object|null} Usuario almacenado o null
 */
export const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error al parsear usuario del localStorage:', error);
    return null;
  }
};

export default {
  login,
  logout,
  refreshToken,
  getCurrentUser,
  isAuthenticated,
  getStoredUser,
};
